import { useState, type ReactNode } from "react";
import type { CmsBlock } from "@/services/pages";
import { sanitizeHtml } from "@/utils/sanitize";

interface ImageProps {
  src?: string;
  alt?: string;
  className?: string;
}

function Img({ src, alt = "", className = "" }: ImageProps) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={`object-cover object-top w-full h-full ${className}`}
    />
  );
}

function HeroBlock({ block }: { block: CmsBlock }) {
  const heading = block.heading as string;
  const subheading = block.subheading as string;
  const image = block.image as string;
  const ctaLabel = block.ctaLabel as string;
  const ctaHref = (block.ctaHref as string) || "#";
  return (
    <section className="relative w-full bg-foreground-950 text-background-50 overflow-hidden">
      {image && (
        <div className="absolute inset-0">
          <Img src={image} alt={heading || ""} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
      )}
      <div className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center px-4 md:px-6">
        <div className="max-w-3xl text-center w-full">
          <span className="inline-block rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold px-4 py-1.5 mb-5">
            {block.kicker || "NOMADLIVINGS"}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold leading-tight">{heading}</h1>
          {subheading && (
            <p className="mt-5 text-base md:text-lg text-background-50/80 max-w-2xl mx-auto">{subheading}</p>
          )}
          {ctaLabel && (
            <a
              href={ctaHref}
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 hover:bg-primary-700"
            >
              {ctaLabel} <i className="ri-arrow-right-line"></i>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function HeadingBlock({ block }: { block: CmsBlock }) {
  const title = block.title as string;
  const body = block.body as string;
  const align = (block.align as string) || "center";
  const alignCls = align === "left" ? "text-left items-start" : "text-center items-center";
  return (
    <section className="py-14 md:py-20 bg-background-50 px-4 md:px-6">
      <div className={`max-w-3xl mx-auto flex flex-col gap-4 ${alignCls}`}>
        {block.kicker && (
          <span className="inline-block rounded-full bg-accent-100 text-accent-900 text-xs font-semibold px-4 py-1.5">
            {block.kicker}
          </span>
        )}
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{title}</h2>
        {body && <p className="text-foreground-700 leading-relaxed">{body}</p>}
      </div>
    </section>
  );
}

function TextBlock({ block }: { block: CmsBlock }) {
  return (
    <section className="py-12 md:py-16 bg-background-100 px-4 md:px-6">
      <div className="max-w-3xl mx-auto space-y-4">
        {block.title && <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{block.title}</h2>}
        {block.body && (
          <div
            className="text-foreground-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-4"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(block.body)) }}
          />
        )}
      </div>
    </section>
  );
}

function StatsBlock({ block }: { block: CmsBlock }) {
  const items = (Array.isArray(block.items) ? block.items : []) as { label: string; value: string }[];
  return (
    <section className="py-14 md:py-20 bg-background-100 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        {block.title && (
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">{block.title}</h2>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl bg-background-50 border border-background-200 p-6 text-center">
              <div className="font-heading text-3xl md:text-4xl font-bold text-accent-700">{it.value}</div>
              <div className="mt-2 text-sm text-foreground-700">{it.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CardsBlock({ block }: { block: CmsBlock }) {
  const items = (Array.isArray(block.items) ? block.items : []) as { title: string; body: string; icon?: string; image?: string }[];
  return (
    <section className="py-14 md:py-20 bg-background-50 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        {block.title && (
          <div className="text-center mb-10">
            {block.kicker && (
              <span className="inline-block rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold px-4 py-1.5 mb-3">
                {block.kicker}
              </span>
            )}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{block.title}</h2>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl bg-background-100 border border-background-200 overflow-hidden">
              {it.image && <div className="h-44 w-full"><Img src={it.image} alt={it.title} /></div>}
              <div className="p-5 md:p-6">
                {it.icon && (
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-accent-100 text-accent-800 text-xl mb-4">
                    <i className={it.icon}></i>
                  </span>
                )}
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">{it.title}</h3>
                {it.body && <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{it.body}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImageTextBlock({ block }: { block: CmsBlock }) {
  const image = block.image as string;
  const reverse = Boolean(block.reverse);
  return (
    <section className="py-14 md:py-20 bg-background-50 px-4 md:px-6">
      <div className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${reverse ? "lg:flex-row-reverse" : ""}`}>
        <div className="rounded-2xl overflow-hidden h-64 md:h-96 bg-background-100">
          <Img src={image} alt={(block.title as string) || ""} />
        </div>
        <div className="space-y-4">
          {block.kicker && (
            <span className="inline-block rounded-full bg-accent-100 text-accent-900 text-xs font-semibold px-4 py-1.5">
              {block.kicker}
            </span>
          )}
          {block.title && <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{block.title}</h2>}
          {block.body && (
            <div
              className="text-foreground-700 leading-relaxed [&_p]:mb-4"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(block.body)) }}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function FaqBlock({ block }: { block: CmsBlock }) {
  const items = (Array.isArray(block.items) ? block.items : []) as { q: string; a: string }[];
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="py-14 md:py-20 bg-background-100 px-4 md:px-6">
      <div className="max-w-3xl mx-auto">
        {block.title && <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">{block.title}</h2>}
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={open === i}
              >
                <span className="font-medium text-foreground-950 text-sm md:text-base">{it.q}</span>
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary-100 text-secondary-900 shrink-0">
                  <i className={open === i ? "ri-subtract-line" : "ri-add-line"}></i>
                </span>
              </button>
              {open === i && (
                <div className="px-5 pb-5 text-sm text-foreground-700 leading-relaxed">{it.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBlock({ block }: { block: CmsBlock }) {
  const image = block.image as string;
  return (
    <section className="py-14 md:py-20 px-4 md:px-6">
      <div className="max-w-6xl mx-auto rounded-3xl bg-foreground-950 text-background-50 overflow-hidden relative">
        {image && (
          <div className="absolute inset-0">
            <Img src={image} alt="" />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
        )}
        <div className="relative px-6 md:px-12 py-12 md:py-16 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold">{block.title}</h2>
          {block.body && <p className="mt-4 text-background-50/80 max-w-2xl mx-auto">{block.body}</p>}
          {block.label && (
            <a
              href={(block.href as string) || "#"}
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 hover:bg-primary-700"
            >
              {block.label} <i className="ri-arrow-right-line"></i>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function BannerBlock({ block }: { block: CmsBlock }) {
  const image = block.image as string;
  return (
    <section className="w-full py-14 md:py-20 bg-background-50 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        {block.title && <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-6">{block.title}</h2>}
        {image && <div className="w-full h-64 md:h-80 rounded-3xl overflow-hidden"><Img src={image} alt={block.title as string} /></div>}
      </div>
    </section>
  );
}

const RENDERERS: Record<string, (props: { block: CmsBlock }) => ReactNode> = {
  hero: HeroBlock,
  heading: HeadingBlock,
  text: TextBlock,
  stats: StatsBlock,
  cards: CardsBlock,
  image_text: ImageTextBlock,
  faq: FaqBlock,
  cta: CtaBlock,
  banner: BannerBlock,
};

/**
 * Renders a CMS page's structured `sections` array into live page sections.
 * Unknown block types are skipped safely.
 */
export default function PageRenderer({ sections }: { sections: CmsBlock[] }) {
  if (!Array.isArray(sections) || !sections.length) return null;
  return (
    <>
      {sections.map((block, i) => {
        const Renderer = RENDERERS[block.type];
        if (!Renderer) return null;
        return <Renderer key={i} block={block} />;
      })}
    </>
  );
}